import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import {
  DataBlocks,
  I18n,
  Switch,
  Button,
  Link,
  Heading,
  ErrorBlock,
} from 'components';

import { formatLogin } from 'utils/formatString';

import { USER_TYPES } from '../../constants';

import UnlinkAccountConfirmationPopup from '../UnlinkAccountConfirmationPopup';
import IncomingLinkageRequest from '../IncomingLinkageRequest';
import OutgoingLinkageRequest from '../OutgoingLinkageRequest';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

const isFttb = (type) => type === USER_TYPES.FTTB;

class MasterAccounts extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      deletingLogin: '',
      deletingType: '',
    };
  }

  deleteAccountLink = () => {
    this.props.deleteMasterAccountLink(this.state.deletingLogin);
  };

  openConfirmationPopup(deletingLogin, deletingType) {
    this.setState({
      deletingLogin,
      deletingType,
    });

    this.props.toggleConfirmationDeletingPopup(true);
  }

  hasData() {
    const {
      isEnabled,
      accounts,
      incomingLinkageRequests,
      outgoingLinkageRequests,
    } = this.props;

    return isEnabled !== null
           && accounts
           && incomingLinkageRequests
           && outgoingLinkageRequests;
  }

  isEmpty() {
    const {
      accounts,
      incomingLinkageRequests,
      outgoingLinkageRequests,
    } = this.props;

    return !accounts.length
           && !incomingLinkageRequests.length
           && !outgoingLinkageRequests.length;
  }

  render() {
    const {
      accounts,
      isEnabled,
      updateProhibition,
      linkMasterAccount,
      isMasterAccountLinkDeleting,
      isProhibitionUpdating,
      incomingLinkageRequests,
      outgoingLinkageRequests,
      cancelRequest,
      updateLinkageRequest,
      error,
      refreshAction,
      isConfirmationDeletingPopupOpened,
      toggleConfirmationDeletingPopup,
      deleteMasterAccountLink,
    } = this.props;

    const {
      deletingLogin,
      deletingType,
    } = this.state;

    if (error) {
      return (
        <ErrorBlock
          title={<I18n path="settings.numberManagement.masterAccounts.title" />}
          refreshAction={refreshAction}
        />
      );
    }

    return (
      <DataBlocks.Block
        isLoading={!this.hasData()}
        title={<I18n path="settings.numberManagement.masterAccounts.title" />}
      >
        {this.hasData() && !this.isEmpty()
          ? (
            <DataBlocks.Table>
              {accounts.map(({ login, type }) => (
                <DataBlocks.TableRow
                  key={`master-account-${login}`}
                >
                  <DataBlocks.TableCell>
                    {formatLogin(login, isFttb(type))}
                  </DataBlocks.TableCell>
                  <DataBlocks.TableCell
                    className={cx('actionCell')}
                  >
                    <Link
                      onClick={() => this.openConfirmationPopup(login)}
                    >
                      <I18n path="settings.numberManagement.masterAccounts.prohibitPhoneNumber" />
                    </Link>
                  </DataBlocks.TableCell>
                </DataBlocks.TableRow>
              ))}
              {incomingLinkageRequests.map((linkageRequest) => (
                <IncomingLinkageRequest
                  key={`incoming-linkage-request-for-master-${linkageRequest.initiatorName}`}
                  messageText={<I18n path="settings.numberManagement.masterAccounts.incomingLinkageRequestHint" />}
                  acceptText={<I18n path="settings.numberManagement.allow" />}
                  declineText={<I18n path="settings.numberManagement.decline" />}
                  {...linkageRequest}
                  updateLinkageRequest={updateLinkageRequest}
                />
              ))}
              {outgoingLinkageRequests.map((linkageRequest) => (
                <OutgoingLinkageRequest
                  key={`outgoing-linkage-request-for-master-${linkageRequest.destinationName}`}
                  messageText={<I18n path="settings.numberManagement.masterAccounts.outgoingLinkageRequestHint" />}
                  cancelRequestText={<I18n path="settings.numberManagement.cancelRequest" />}
                  {...linkageRequest}
                  cancelRequest={cancelRequest}
                />
              ))}
            </DataBlocks.Table>
          )
          : (
            <div className={cx('emptyMessage')}>
              <I18n path="settings.numberManagement.masterAccounts.emptyMessage" />
            </div>
          )}
        <div className={cx('footer')}>
          <Button
            className={['default', cx('footerButton')]}
            onClick={linkMasterAccount}
          >
            <I18n path="settings.numberManagement.masterAccounts.sendInvitation" />
          </Button>
          <Switch
            className={cx('prohibitMasterAccounts')}
            name="prohibit-master-accounts"
            checked={!isEnabled}
            onChange={() => updateProhibition(!isEnabled)}
            isLoading={isProhibitionUpdating}
          >
            <I18n path="settings.numberManagement.masterAccounts.prohibitMasterAccounts" />
          </Switch>
        </div>
        <UnlinkAccountConfirmationPopup
          isConfirmationPopupOpened={isConfirmationDeletingPopupOpened}
          isAccountLinkDeleting={isMasterAccountLinkDeleting}
          onClose={() => toggleConfirmationDeletingPopup(false)}
          onDelete={() => deleteMasterAccountLink(deletingLogin)}
          deleteButtonText={<I18n path="settings.numberManagement.unlinkMasterAccountButtonText" />}
          cancelButtonText={<I18n path="settings.numberManagement.cancelChanges" />}
        >
          <Heading level={3}>
            <I18n
              path="settings.numberManagement.associatingAccount.unlinkMasterConfirmationMessage"
              vars={{ login: formatLogin(deletingLogin, isFttb(deletingType)) }}
            />
          </Heading>
          <Heading level={6}>
            <I18n path="settings.numberManagement.associatingAccount.unlinkMasterConfirmationDescription" />
          </Heading>
        </UnlinkAccountConfirmationPopup>
      </DataBlocks.Block>
    );
  }
}

MasterAccounts.propTypes = {
  error: PropTypes.string,
  accounts: PropTypes.array,
  isEnabled: PropTypes.bool,
  updateProhibition: PropTypes.func,
  linkMasterAccount: PropTypes.func,
  deleteMasterAccountLink: PropTypes.func,
  isMasterAccountLinkDeleting: PropTypes.bool,
  isProhibitionUpdating: PropTypes.bool,
  incomingLinkageRequests: PropTypes.array,
  outgoingLinkageRequests: PropTypes.array,
  cancelRequest: PropTypes.func,
  updateLinkageRequest: PropTypes.func,
  refreshAction: PropTypes.func,
  isConfirmationDeletingPopupOpened: PropTypes.bool,
  toggleConfirmationDeletingPopup: PropTypes.func,
};

export default MasterAccounts;
